import React, { useState, useEffect } from 'react';
import { FormControl, FormHelperText } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import {
    PasslyValidatedPassword,
    PasslyCheckbox,
    PasslyTextField
} from '@app/components';
import { AgentConfigTypeEnum, IWindowsLogonConfig, Order, IOrgMapping, IGroupsDTO } from '@app/models';
import { offlineLoginMin, offlineLoginMax, localStore } from '@app/helpers';
import { groupsService } from '@app/services';
import { AxiosResponse } from 'axios';
import { useDispatch } from 'react-redux';
import { changeOrganizationSelect } from '@app/store/actions';
import { InputNumber } from '@itglue/kaseya-ui';

interface IProps {
    updateProtectedConfigCallback: Function;
    updateConfigCallback: Function;
    formValidCallback: Function;
    isEditView: boolean;
    overridePass: string;
    editAgent?: IWindowsLogonConfig;
}

const defaultConfig: IWindowsLogonConfig = {
    LogonWithPush: true,
    RDPOnly2FA: false,
    HasOverrideGroup: false,
    OverrideGroupRequires2FA: false,
    HasOverridePassword: false,
    HideRemoteSessions: false,
    AllowOfflineAccess: false,
    AllowOfflineAccessForXDays: 1,
    HasAliasRedirect: false,
    OrgMappings: []
};

const defaultOrgMapping: IOrgMapping = {
    Domain: '',
    AliasFor: '',
    client_id: '',
    client_secret: '',
    homeRealm: ''
};

const WindowsLogonConfig: React.SFC<IProps> = ({
    updateProtectedConfigCallback,
    updateConfigCallback,
    formValidCallback,
    isEditView,
    overridePass,
    editAgent = defaultConfig
}) => {
    const localStorageOrganization = localStore.getItem('org_filter') ? localStore.getItem('org_filter') : '';
    const [agentConfig, setAgentConfig] = useState<IWindowsLogonConfig>(isEditView ? editAgent : defaultConfig);
    const [overridePassword, setOverridePassword] = useState(overridePass);
    const [passwordError, setPasswordError] = useState(false);
    const [groupsDTO, setGroupsDTO] = useState<IGroupsDTO[]>([]);
    const [groupsDTOCount, setGroupsDTOCount] = useState(0);
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        updateProtectedConfigCallback(overridePassword, AgentConfigTypeEnum.windowsLogonConfig);
    }, [overridePassword]);

    useEffect(() => {
        if (localStorageOrganization !== '' && localStorageOrganization !== null) {
            dispatch(changeOrganizationSelect(localStorageOrganization));
        }
    }, []);

    useEffect(() => {
        if (
            (agentConfig.HasOverridePassword && (overridePassword === '' || passwordError)) ||
            (agentConfig.AllowOfflineAccess &&
                (agentConfig.AllowOfflineAccessForXDays < offlineLoginMin || agentConfig.AllowOfflineAccessForXDays > offlineLoginMax))
        ) {
            formValidCallback(false);
        } else {
            updateConfigCallback(agentConfig);
            formValidCallback(true);
        }
    }, [agentConfig, passwordError, overridePassword]);

    const onCheckboxChange = (checked: boolean, e: any) => {
        const { name } = e.target;
        let agentCopy = Object.assign({}, agentConfig);
        agentCopy[name] = checked;

        if (name === 'HasAliasRedirect') {
            if (checked) {
                if (agentCopy.OrgMappings === undefined) {
                    agentCopy.OrgMappings = [defaultOrgMapping];
                } else {
                    agentCopy.OrgMappings.push(defaultOrgMapping);
                }
            } else {
                agentCopy.OrgMappings = [];
            }
        }
        setAgentConfig(agentCopy);
    };

    const onOverridePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOverridePassword(e.target.value);
        updateProtectedConfigCallback(e.target.value);
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;
        let agentCopy = Object.assign({}, agentConfig);
        agentCopy[name] = value;
        setAgentConfig(agentCopy);
        updateConfigCallback(agentCopy);
    };

    const onAliasFormChange = (e: any) => {
        const { name, value } = e.target;
        let agentCopy = Object.assign({}, agentConfig);

        if (agentCopy.OrgMappings.length === 0) {
            agentCopy.OrgMappings.push();
        }

        agentCopy.OrgMappings[0][name] = value;
        setAgentConfig(agentCopy);
        updateConfigCallback(agentCopy);
    };

    const fetchMoreData = (skipCount: number, currentOrgId: string, isReset?: boolean) => {
        groupsService.getGroupsDTO('Name', Order.asc, currentOrgId, skipCount, isReset).then((response: AxiosResponse) => {
            setGroupsDTO(groupsDTO.concat(response.data.value));
            response.data['@odata.count'] && setGroupsDTOCount(response.data['@odata.count']);
        });
    };

    const onFormChangeNumberParam = (e: any) => {
        const { name, value } = e.target;
        let agentCopy = Object.assign({}, agentConfig);

        agentCopy[name] = Number(value);
        setAgentConfig(agentCopy);
        updateConfigCallback(agentCopy);
    };

    return (
        <>
            <div className='rowcontainer'>
                <div className='configpanel-left configpanel-left--windows-logon'>
                    <div className='container'>
                        <form className='modal__form' method='post'>
                            <PasslyCheckbox
                                name='RDPOnly2FA'
                                checked={agentConfig.RDPOnly2FA}
                                onChange={onCheckboxChange}
                                label={translation('ENFORCE_2FA_ON_RDP')}
                            />
                            <PasslyCheckbox
                                name='LogonWithPush'
                                checked={agentConfig.LogonWithPush}
                                onChange={onCheckboxChange}
                                label={translation('AUTOMATICALLY_ATTEMPT_PUSH_ON_LOGON')}
                            />
                            <PasslyCheckbox
                                name='HasOverridePassword'
                                checked={agentConfig.HasOverridePassword}
                                onChange={onCheckboxChange}
                                label={translation('ALLOW_OVERRIDE_PASSWORD')}
                            />

                            {agentConfig.HasOverridePassword && (
                                <div className='modal__form-row'>
                                    <PasslyValidatedPassword
                                        value={overridePassword}
                                        onChange={onOverridePasswordChange}
                                        allowReveal
                                    />
                                </div>
                            )}

                            <PasslyCheckbox
                                name='HasOverrideGroup'
                                checked={agentConfig.HasOverrideGroup}
                                onChange={onCheckboxChange}
                                label={translation('ALLOW_OVERRIDE_GROUP')}
                            />

                            {agentConfig.HasOverrideGroup && (
                                <PasslyCheckbox
                                    name='OverrideGroupRequires2FA'
                                    checked={agentConfig.OverrideGroupRequires2FA}
                                    onChange={onCheckboxChange}
                                    label={translation('OVERRIDE_GROUP_REQUIRES_2FA')}
                                />
                            )}

                            <PasslyCheckbox
                                name='HideRemoteSessions'
                                checked={agentConfig.HideRemoteSessions}
                                onChange={onCheckboxChange}
                                label={translation('HIDE_REMOTE_SESSIONS')}
                            />
                            <PasslyCheckbox
                                name='HasAliasRedirect'
                                checked={agentConfig.HasAliasRedirect}
                                onChange={onCheckboxChange}
                                label={translation('HAS_ALIAS_REDIRECT')}
                            />

                            {agentConfig.HasAliasRedirect && (
                                <>
                                    <FormControl>
                                        <PasslyTextField
                                            fullWidth
                                            name='Domain'
                                            label={'ALIAS_DOMAIN'}
                                            value={agentConfig.OrgMappings[0].Domain}
                                            onChange={onAliasFormChange}
                                            id='aliasDomain-input'
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <PasslyTextField
                                            fullWidth
                                            name='AliasFor'
                                            label={'ALIAS_REDIRECT'}
                                            value={agentConfig.OrgMappings[0].AliasFor}
                                            onChange={onAliasFormChange}
                                            id='aliasFor-input'
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <PasslyTextField
                                            fullWidth
                                            name='homeRealm'
                                            label={'ALIAS_HOMEREALM'}
                                            value={agentConfig.OrgMappings[0].homeRealm}
                                            onChange={onAliasFormChange}
                                            id='homeRealm-input'
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <PasslyTextField
                                            fullWidth
                                            name='client_id'
                                            label={'ALIAS_CLIENT_ID'}
                                            value={agentConfig.OrgMappings[0].client_id}
                                            onChange={onAliasFormChange}
                                            id='clientId-input'
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <PasslyTextField
                                            fullWidth
                                            name='client_secret'
                                            label={'ALIAS_CLIENT_KEY'}
                                            value={agentConfig.OrgMappings[0].client_secret}
                                            onChange={onAliasFormChange}
                                            id='aliasClientSecret-input'
                                        />
                                    </FormControl>
                                </>
                            )}

                            <PasslyCheckbox
                                name='AllowOfflineAccess'
                                checked={agentConfig.AllowOfflineAccess}
                                onChange={onCheckboxChange}
                                label={translation('ALLOW_OFFLINE_ACCESS')}
                            />

                            {agentConfig.AllowOfflineAccess && (
                                <>
                                    <FormControl
                                        required
                                        error={
                                            agentConfig.AllowOfflineAccessForXDays < offlineLoginMin ||
                                            agentConfig.AllowOfflineAccessForXDays > offlineLoginMax
                                        }
                                    >
                                        <InputNumber
                                            id='offlinedays-input'
                                            label={translation('ALLOW_OFFLINE_ACCESS_FOR_X_DAYS_WITH_VALID_DAYS')}
                                            name='AllowOfflineAccessForXDays'
                                            value={agentConfig.AllowOfflineAccessForXDays}
                                            onValueChange={onFormChangeNumberParam}
                                            showButtons
                                            idecrementButtonClassName={''}
                                            min={offlineLoginMin}
                                            max={offlineLoginMax}
                                        />
                                        <FormHelperText>{translation('ALLOW_OFFLINE_ACCESS_WARNING')}</FormHelperText>
                                    </FormControl>
                                </>
                            )}
                        </form>
                    </div>
                </div>
                <div className='configpanel-right configpanel-right--windows-logon'>
                    <div dangerouslySetInnerHTML={{ __html: translation('WINDOWS_LOGON_CONFIG_HELPER_HEADER') }} />
                </div>
            </div>
        </>
    );
};

export default WindowsLogonConfig;
